import { NO_NAMES_NOTE } from '@aera/content/client';
import { MAX_TEXT } from '@aera/schema';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, fresh, type AnswerProps } from './common.js';

/** Free text (A1b, B4, I3). Voice input comes from the phone's own keyboard; prompt chips insert a cue. */
export function TextQuestion({ rq, value, onChange, lang }: AnswerProps) {
  const { t } = useTranslation();
  const area = useRef<HTMLTextAreaElement>(null);

  const insert = (chip: string) => {
    const sep = lang === 'zh' ? '：' : ': ';
    const current = value.text ?? '';
    const prefix = current && !current.endsWith('\n') ? '\n' : '';
    const next = `${current}${prefix}${chip}${sep}`;
    onChange({ ...fresh(value), text: next }, { debounce: true });
    requestAnimationFrame(() => {
      const el = area.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.length, next.length);
    });
  };

  return (
    <div className="space-y-3">
      {rq.chips && rq.chips.length > 0 && (
        <div>
          <p className="mb-2 text-[13px] text-ink-2">{t('question.chipsLabel')}</p>
          <div className="flex flex-wrap gap-2">
            {rq.chips.map((c) => (
              <Chip key={c} role="button" onClick={() => insert(c)}>
                {c}
              </Chip>
            ))}
          </div>
        </div>
      )}
      <textarea
        ref={area}
        aria-labelledby={`q-${rq.id}-label`}
        rows={5}
        maxLength={MAX_TEXT}
        value={value.text ?? ''}
        placeholder={rq.placeholder ?? t('question.textPlaceholder')}
        onChange={(e) => onChange({ ...fresh(value), text: e.target.value }, { debounce: true })}
        className="w-full resize-y rounded-2xl border border-hair bg-white px-4 py-3 text-[16px] leading-relaxed outline-none transition-colors placeholder:text-ink-3 focus:border-indigo"
      />
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-[13px] text-ink-2">
        <span>{rq.hint}</span>
        {rq.noNamesNote && <span>{NO_NAMES_NOTE[lang]}</span>}
      </div>
    </div>
  );
}
